import { PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { NavLink, useSearchParams } from 'react-router';
import { Badge } from 'softcat:components/badge.tsx';
import { CertificateStatus } from 'softcat:components/certificate-status.tsx';
import { ConfirmationModal } from 'softcat:components/confirmation-modal.tsx';
import { DataTable } from 'softcat:components/data-table.tsx';
import { SearchParametersOrderBy } from 'softcat:components/search-parameters-order-by.tsx';
import { SearchParametersPagination } from 'softcat:components/search-parameters-pagination.tsx';
import { SearchParametersSearchField } from 'softcat:components/search-parameters-search-field.tsx';
import { Spinner } from 'softcat:components/spinner.tsx';
import { BadgeState } from 'softcat:config/badge-state.ts';
import { useTerritory } from 'softcat:hooks/use-territory.ts';
import { CertificateService } from 'softcat:services/certificate.ts';
import useSWR, { preload } from 'swr';

export const Certificates = () => {
    const intl = useIntl();
    const { activeTerritory } = useTerritory();
    const [searchParameters] = useSearchParams();
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [selectedCertificate, setSelectedCertificate] = useState({ id: '', name: '' });
    const [onRemoveErrorMessage, setOnRemoveErrorMessage] = useState('');
    const limit = Number.parseInt(searchParameters.get('limit') || '10');
    const page = Number.parseInt(searchParameters.get('page') || '1');
    const query = searchParameters.get('query') || '';
    const orderBy = searchParameters.get('orderBy') || '';

    const { data, error, isLoading, mutate } = useSWR(
        { key: `${activeTerritory}-CertificateServiceFindAll`, limit, orderBy, page: page - 1, query },
        CertificateService.findAll,
    );

    const columns = useMemo(() => {
        return [
            { key: 'id', value: intl.formatMessage({ id: 'CERTIFICATES_PAGE_COLUMN_ID' }), visible: true },
            { key: 'type', value: intl.formatMessage({ id: 'CERTIFICATES_PAGE_COLUMN_TYPE' }), visible: true },
            { key: 'name', value: intl.formatMessage({ id: 'CERTIFICATES_PAGE_COLUMN_NAME' }), visible: true },
            { key: 'active', value: intl.formatMessage({ id: 'CERTIFICATES_PAGE_COLUMN_STATUS' }), visible: true },
            { key: 'status', value: intl.formatMessage({ id: 'CERTIFICATES_PAGE_COLUMN_CERTIFICATE_STATUS' }), visible: true },
            { key: 'actions', value: intl.formatMessage({ id: 'CERTIFICATES_PAGE_COLUMN_ACTIONS' }), visible: false },
        ];
    }, [intl]);

    const removeCertificate = async () => {
        try {
            await CertificateService.remove({ id: selectedCertificate.id });
            await mutate();

            setShowConfirmDelete(false);
        } catch (error) {
            setOnRemoveErrorMessage(`${intl.formatMessage({ id: 'CERTIFICATES_PAGE_CONFIRM_DELETE_ERROR' })}: ${(error as Error).message}`);
        }
    };

    const rows = useMemo(() => {
        return (
            data?.map((certificate, index) => {
                return {
                    actions: (
                        <div className={'flex gap-4 justify-self-end'}>
                            <NavLink
                                data-testid={`edit-certificate-${index}`}
                                data-tooltip-content={intl.formatMessage({ id: 'TOOLTIP_EDIT' })}
                                data-tooltip-id={'app-tooltip'}
                                onMouseEnter={() => {
                                    return preload({ id: certificate.id, key: `${activeTerritory}-CertificateServiceFindOne` }, CertificateService.findOne);
                                }}
                                to={`edit/${certificate.id}`}
                                type="button"
                            >
                                <PencilIcon className="size-5 scale-75 text-gray-800 transition-all hover:scale-100 hover:text-indigo-600" />
                            </NavLink>
                            <button
                                data-testid={`delete-certificate-${index}`}
                                data-tooltip-content={intl.formatMessage({ id: 'TOOLTIP_DELETE' })}
                                data-tooltip-id={'app-tooltip'}
                                onClick={() => {
                                    setSelectedCertificate({ id: certificate.id, name: certificate.name });
                                    setOnRemoveErrorMessage('');
                                    setShowConfirmDelete(true);
                                }}
                            >
                                <TrashIcon className="size-5 scale-75 text-gray-800 transition-all hover:scale-100 hover:text-indigo-600" />
                            </button>
                        </div>
                    ),
                    active: certificate.active ? (
                        <Badge status={BadgeState.VALID}>
                            <FormattedMessage id={'CERTIFICATES_PAGE_CERTIFICATE_ACTIVE'} />
                        </Badge>
                    ) : (
                        <Badge status={BadgeState.ERROR}>
                            <FormattedMessage id={'CERTIFICATES_PAGE_CERTIFICATE_INACTIVE'} />
                        </Badge>
                    ),
                    id: certificate.id,
                    name: certificate.name,
                    status: <CertificateStatus certificateExpiry={certificate.expiresAt} />,
                    type: certificate.type,
                    validFrom: certificate.validFrom,
                };
            }) || []
        );
    }, [activeTerritory, data, intl]);

    return (
        <div className="space-y-6 px-4">
            <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                    <h1 className="text-base font-semibold leading-6 text-gray-900">
                        <FormattedMessage id={'CERTIFICATES_PAGE_HEADING'} />
                    </h1>

                    <p className="mt-2 text-sm text-gray-700">
                        <FormattedMessage id={'CERTIFICATES_PAGE_DESCRIPTION'} />
                    </p>
                </div>

                <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                    <NavLink
                        className="flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        to={'create'}
                        type="button"
                    >
                        <PlusIcon className={'size-3'} />
                        <FormattedMessage id={'CERTIFICATES_PAGE_ADD_BUTTON'} />
                    </NavLink>
                </div>
            </div>

            <div className={'flex items-center'}>
                <div className={'grow'}>
                    <SearchParametersSearchField placeholder={intl.formatMessage({ id: 'CERTIFICATES_PAGE_SEARCH_PLACEHOLDER' })} />
                </div>

                <div className={'pl-4'}>
                    <SearchParametersOrderBy
                        options={[
                            { label: intl.formatMessage({ id: 'CERTIFICATES_PAGE_ORDER_BY_NONE' }), value: '' },
                            { label: intl.formatMessage({ id: 'CERTIFICATES_PAGE_ORDER_BY_TYPE_DESC' }), value: 'type:desc' },
                            { label: intl.formatMessage({ id: 'CERTIFICATES_PAGE_ORDER_BY_TYPE_ASC' }), value: 'type:asc' },
                            { label: intl.formatMessage({ id: 'CERTIFICATES_PAGE_ORDER_BY_NAME_DESC' }), value: 'name:desc' },
                            { label: intl.formatMessage({ id: 'CERTIFICATES_PAGE_ORDER_BY_NAME_ASC' }), value: 'name:asc' },
                            { label: intl.formatMessage({ id: 'CERTIFICATES_PAGE_ORDER_BY_STATUS_DESC' }), value: 'status:desc' },
                            { label: intl.formatMessage({ id: 'CERTIFICATES_PAGE_ORDER_BY_STATUS_ASC' }), value: 'status:asc' },
                            { label: intl.formatMessage({ id: 'CERTIFICATES_PAGE_ORDER_BY_EXPIRY_DATE_DESC' }), value: 'expiresAt:desc' },
                            { label: intl.formatMessage({ id: 'CERTIFICATES_PAGE_ORDER_BY_EXPIRY_DATE_ASC' }), value: 'expiresAt:asc' },
                        ]}
                    />
                </div>
            </div>

            {error && (
                <p className="mt-2 text-sm text-gray-700">
                    <FormattedMessage id={'CERTIFICATES_PAGE_ERROR_LOADING_DATA'} />
                </p>
            )}

            {isLoading && <Spinner />}

            {!error && !isLoading && (
                <div>
                    {rows.length > 0 ? (
                        <>
                            <div className={'overflow-auto'}>
                                <DataTable columns={columns} rows={rows} />
                            </div>

                            <div className={'border-t border-t-gray-200 pt-2'}>
                                <SearchParametersPagination total={rows.length} />
                            </div>
                        </>
                    ) : (
                        <p className="mt-2 text-sm text-gray-700">
                            <FormattedMessage id={'CERTIFICATES_PAGE_NO_RESULTS'} />
                        </p>
                    )}
                </div>
            )}

            <ConfirmationModal
                confirmButtonText={intl.formatMessage({ id: 'BUTTON_DELETE' })}
                confirmErrorMessage={onRemoveErrorMessage}
                content={intl.formatMessage({ id: 'CERTIFICATES_PAGE_CONFIRM_DELETE_DESCRIPTION' }, { certificate: selectedCertificate.name })}
                handleClose={() => {
                    setShowConfirmDelete(false);
                }}
                handleConfirm={async () => {
                    await removeCertificate();
                }}
                header={intl.formatMessage({ id: 'CERTIFICATES_PAGE_CONFIRM_DELETE_TITLE' })}
                isVisible={showConfirmDelete}
            />
        </div>
    );
};
